import React from 'react';
import { Table, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { DeliveryQueues, DeliveryQueueRow } from '@/services/salesDashboard.service';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { EmptyQueueState } from './EmptyQueueState';

const cols = (navigate: (p: string) => void) => [
  { title: 'DN #', dataIndex: 'delivery_note_number', key: 'dn', width: 120 },
  { title: 'Date', dataIndex: 'delivery_date', key: 'date', width: 100 },
  { title: 'Customer', dataIndex: 'customer_name', key: 'customer', ellipsis: true },
  {
    title: '',
    key: 'action',
    width: 70,
    render: (_: unknown, r: DeliveryQueueRow) => (
      <a onClick={() => navigate(`/growth/delivery-notes/${r.id}`)}>View</a>
    ),
  },
];

export const DeliveryQueuesCard: React.FC<{ queues: DeliveryQueues }> = ({ queues }) => {
  const navigate = useNavigate();
  const columns = cols(navigate);
  const total =
    queues.delivered_not_invoiced.length +
    queues.partially_invoiced.length +
    queues.in_transit.length +
    queues.doc_package_pending.length;

  return (
    <ControlTowerSectionCard
      zone="deliveries"
      sectionId="delivery-queues"
      title={`Delivery & dispatch (${total})`}
    >
      {total === 0 ? (
        <EmptyQueueState message="All deliveries are on track for invoicing and documentation." />
      ) : (
        <Tabs
          size="small"
          items={[
            {
              key: 'not_inv',
              label: `Not invoiced (${queues.delivered_not_invoiced.length})`,
              children: (
                <ControlTowerSubQueuePanel
                  zone="deliveryNotInvoiced"
                  title="Delivered, not invoiced"
                  count={queues.delivered_not_invoiced.length}
                >
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={queues.delivered_not_invoiced}
                    columns={columns}
                  />
                </ControlTowerSubQueuePanel>
              ),
            },
            {
              key: 'partial',
              label: `Partial invoice (${queues.partially_invoiced.length})`,
              children: (
                <ControlTowerSubQueuePanel
                  zone="deliveryPartial"
                  title="Partially invoiced"
                  count={queues.partially_invoiced.length}
                >
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={queues.partially_invoiced}
                    columns={columns}
                  />
                </ControlTowerSubQueuePanel>
              ),
            },
            {
              key: 'transit',
              label: `In transit (${queues.in_transit.length})`,
              children: (
                <ControlTowerSubQueuePanel
                  zone="deliveryInTransit"
                  title="In transit"
                  count={queues.in_transit.length}
                >
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={queues.in_transit}
                    columns={columns}
                  />
                </ControlTowerSubQueuePanel>
              ),
            },
            {
              key: 'doc',
              label: `Doc package (${queues.doc_package_pending.length})`,
              children: (
                <ControlTowerSubQueuePanel
                  zone="deliveryDocPackage"
                  title="Document package pending"
                  count={queues.doc_package_pending.length}
                >
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={queues.doc_package_pending}
                    columns={columns}
                  />
                </ControlTowerSubQueuePanel>
              ),
            },
          ]}
        />
      )}
    </ControlTowerSectionCard>
  );
};
